import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  Chip,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Avatar,
  CircularProgress,
  Grid,
  Tooltip,
  Menu
} from '@mui/material';
import {
  Edit as EditIcon,
  Block as BlockIcon,
  CheckCircle as VerifyIcon,
  Cancel as UnverifyIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import { userAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import moment from 'moment';

const UserList = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    search: '',
    gender: '',
    status: ''
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [actionUser, setActionUser] = useState(null);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await userAPI.getUsers({
        page: page + 1,
        limit: rowsPerPage,
        ...filters
      });
      setUsers(response.data.data);
      setTotal(response.data.pagination.total);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, rowsPerPage, filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const handleBlockUser = async (user) => {
    try {
      await userAPI.blockUser(user.id, !user.is_blocked);
      toast.success(`User ${user.is_blocked ? 'unblocked' : 'blocked'} successfully`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleVerifyUser = async (user) => {
    try {
      await userAPI.verifyUser(user.id);
      toast.success('User verified successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to verify user');
    }
  };

  const handleUnverifyUser = async (user) => {
    try {
      await userAPI.unverifyUser(user.id);
      toast.success('User unverified successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to unverify user');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    try {
      await userAPI.deleteUser(selectedUser.id);
      toast.success('User deleted successfully');
      setDeleteDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleMenuOpen = (event, user) => {
    setAnchorEl(event.currentTarget);
    setActionUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActionUser(null);
  };

  const getStatusChip = (user) => {
    if (user.is_blocked) {
      return <Chip label="Blocked" color="error" size="small" />;
    }
    if (user.is_verified) {
      return <Chip label="Verified" color="success" size="small" />;
    }
    return <Chip label="Unverified" color="warning" size="small" />;
  };

  const getSubscriptionStatus = (user) => {
    const hasActiveSubscription = user.subscription_status === 'active' && 
                                  user.expiry_date && 
                                  new Date(user.expiry_date) > new Date();
    
    if (hasActiveSubscription) {
      return <Chip label="Premium" color="primary" size="small" />;
    }
    return <Chip label="Free" color="default" size="small" />;
  };

  const formatPhoneWithCode = (user) => {
    const countryCode = user.country_code || '+977';
    const phone = user.phone || '';
    return `${countryCode} ${phone}`;
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          User Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={fetchUsers}
        >
          Refresh
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search"
              placeholder="Name or Phone"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Gender</InputLabel>
              <Select
                value={filters.gender}
                label="Gender"
                onChange={(e) => handleFilterChange('gender', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="verified">Verified</MenuItem>
                <MenuItem value="unverified">Unverified</MenuItem>
                <MenuItem value="blocked">Blocked</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Gender</TableCell>
                <TableCell>Age</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Subscription</TableCell>
                <TableCell>Joined</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.id}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar src={user.profile_photo_url} sx={{ width: 32, height: 32 }}>
                          {user.first_name?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2">
                            {user.first_name} {user.last_name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            ID: {user.id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatPhoneWithCode(user)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Code: {user.country_code || '+977'}
                      </Typography>
                    </TableCell>
                    <TableCell>{user.gender || 'N/A'}</TableCell>
                    <TableCell>
                      {user.date_of_birth ? moment().diff(moment(user.date_of_birth), 'years') : 'N/A'}
                    </TableCell>
                    <TableCell>{getStatusChip(user)}</TableCell>
                    <TableCell>
                      <Box>
                        {getSubscriptionStatus(user)}
                        {user.subscription_status === 'active' && user.expiry_date && (
                          <Typography variant="caption" display="block" color="textSecondary">
                            Expires: {moment(user.expiry_date).format('YYYY-MM-DD')}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{moment(user.created_at).format('YYYY-MM-DD')}</TableCell>
                    <TableCell align="center">
                      <Box display="flex" justifyContent="center" gap={0.5}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/users/${user.id}`)}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {!user.is_verified ? (
                          <Tooltip title="Verify User">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleVerifyUser(user)}
                            >
                              <VerifyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Unverify User">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleUnverifyUser(user)}
                            >
                              <UnverifyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        <Tooltip title={user.is_blocked ? "Unblock User" : "Block User"}>
                          <IconButton
                            size="small"
                            color={user.is_blocked ? "success" : "warning"}
                            onClick={() => handleBlockUser(user)}
                          >
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Delete User">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setSelectedUser(user);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {selectedUser?.first_name} {selectedUser?.last_name}?
            This action cannot be undone and will delete all user data.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserList;